/**
 * Générateur — Attendu 183 — O1 « Il utilise le vocabulaire du repérage :
 * abscisse, ordonnée, altitude. »
 * Niveau : 4ème | Thème #10 Espace (O1) | Domaine : Espace et géométrie.
 * Source prof : « TANOED EXERCISEUR ESPACE ET GEOMETRIE 4EME », O1.
 *
 * Revue 2026-09-23 (vague 4) : gen REBÂTI, décisions du coordinateur
 * appliquées en mode auto. Attendu de fin de 4e (Eduscol, attendus de fin
 * d'année, p. 9) : « Il utilise le vocabulaire du repérage : abscisse,
 * ordonnée, altitude. » ; exemple de réussite : « Dans un repère de l'espace,
 * il lit les coordonnées d'un point et place un point de coordonnées
 * données. », sur la figure d'un pavé ABCDEFGH et de trois axes gradués
 * partant de A. Cours du professeur (19Espace2 p. 4-5) : « abscisse –
 * ordonnée – altitude » ; « on note dans l'ordre entre parenthèses
 * l'abscisse, l'ordonnée et l'altitude » ; A(0 ; 0 ; 0) … G(5 ; 7 ; 4),
 * K milieu de [FG] = (5 ; 3,5 ; 4).
 *   - Le repère de l'espace n'est plus dans le BO 2025 ; il est dans
 *     l'attendu de 2019 et dans le cours : il est gardé (mode auto, À
 *     ARBITRER).
 *   - Les DIX formats d'avant SORTENT (« Lire les coordonnées », « Placer des
 *     points », « Trouver la coordonnée », « Symétries et coordonnées »,
 *     « Régions et inégalités ») : tous dans le repère du PLAN, aucun ne
 *     servait l'abscisse, l'ordonnée et l'altitude. Lire et placer dans le
 *     plan sont l'automatisme de 4e et le gen 945, les symétries aussi ;
 *     « régions et inégalités » n'a aucune source (ni Eduscol, ni BO, ni
 *     cours) et sa zone teintée donnait la réponse dans 100 % des tirages.
 *   - Cinq cartes NEUVES, toutes sur la figure du cours (svgRepereEspace,
 *     svgEspace.js, partagée avec 184) : pavé de dimensions X (fuyante, 2 à
 *     4), Y (3 à 7), Z (2 à 5) deux à deux distinctes ; trois axes gradués
 *     depuis A, « 0 » et « 1 » écrits SEULEMENT (les nombres au bout des axes,
 *     que le cours écrit, sont les coordonnées de B, D et E : mode auto, À
 *     ARBITRER) ; jamais de cote ; un point hors des sommets est une croix sur
 *     une arête visible, graduée, loin des croisements de la projection, et
 *     l'énoncé dit sur quelle arête il est (une croix seule, en projection,
 *     se lirait à plusieurs endroits de l'espace).
 *       1. « Lire l'abscisse, l'ordonnée ou l'altitude »
 *          (composante_qcm_unique) : « Quelle est l'altitude du point G ? »,
 *          le mot tourne (un tiers chacun) ; choix : les trois coordonnées
 *          du point et 0 ; le point a trois coordonnées distinctes non
 *          nulles (G, ou un point des arêtes [FG], [CG], [GH]).
 *       2. « Que représente ce nombre ? » (role_qcm_unique) : « Dans
 *          G(5 ; 7 ; 4), que représente le nombre 7 ? » ; trois choix,
 *          l'abscisse, l'ordonnée, l'altitude de G ; le nombre n'apparaît
 *          qu'une fois dans le triplet (C, F, G, H ou un point d'arête,
 *          jamais A, B, D, E) ; la position demandée tourne ; jamais « la
 *          longueur AD » en choix (seconde bonne réponse).
 *       3. « Quel point ? » (point_qcm_unique) : « Quel point a pour
 *          coordonnées (0 ; 7 ; 4) ? », toujours le triplet COMPLET (« ordonnée
 *          7 et altitude 0 » avait deux réponses, C et D) ; trois régimes à
 *          parts égales : un sommet et ses trois voisins (ils partagent deux
 *          coordonnées sur trois) ; F et le point K(0 ; X ; Z) de [EH], son
 *          permuté ; un point de [EF] ou [EH] et son permuté sur l'autre
 *          arête. Le permuté incarne echange_xy (DEPP 4e 2023 q.20, 35,9 %)
 *          dans deux tirages sur trois.
 *       4. « Coordonnées communes » (communes_tableau_vf), VENUE de 184 :
 *          quatre lignes « B et F ont la même abscisse. », nombre de vraies
 *          tiré entre 1 et 3 AVANT les lignes (10,0 % sans vraie et 3,0 %
 *          sans fausse avant), quatre paires distinctes (13,9 % de paires
 *          répétées avant), les trois mots présents.
 *       5. « Abscisse, ordonnée, altitude » (mots_texte_trou_drag) : trois
 *          lignes entièrement en KaTeX, « 5 est l'abscisse de G. », dans le
 *          désordre ; étiquettes « l'abscisse », « l'ordonnée », « l'altitude »
 *          et deux pièges nommés, « la longueur » et « la hauteur »
 *          (vocabulaire_des_longueurs : le nombre pris pour une dimension du
 *          pavé), passés par filtrerDistracteurs ; le point a trois
 *          coordonnées distinctes, chaque mot a une seule place.
 *   - Rappel brouillon : aucun (lecture de figure, rien à calculer).
 *   - KaTeX : les triplets (énoncés, lignes à trous) ; les noms de points et
 *     les nombres seuls restent en texte, comme dans le reste du parc.
 *   - Contrat des distracteurs exporté (REGLES_DISTRACTEURS_QCM), codes dans
 *     _meta.params.distracteurs. Aucun solveur test-side ne couvre ce gen ; la
 *     figure porte data-repere et data-points pour un futur solveur.
 * Cinq formats, dont trois QCM.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, makeVraiFaux, filtrerDistracteurs, tex,
} from '../../../utils';
import {
    svgRepereEspace, elementFigure, sommetsRepere, areteVisible, pointLisible,
    tirerDimsRepere, nommerPointsRepere, pointsAretesRepere, tripletLatex, tirerPointTroisValeurs,
} from './svgEspace';

const ATTENDU_ID = 183;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_COMPOSANTE = 'Lire l\'abscisse, l\'ordonnée ou l\'altitude';
const L_ROLE = 'Que représente ce nombre ?';
const L_POINT = 'Quel point ?';
const L_COMMUNES = 'Coordonnées communes';
const L_MOTS = 'Abscisse, ordonnée, altitude';

const MOTS = ['abscisse', 'ordonnée', 'altitude'];
const LE_MOT = ['l\'abscisse', 'l\'ordonnée', 'l\'altitude'];
/** L'erreur d'un choix qui ne diffère de la clé que par la coordonnée i. */
const OUBLI = ['abscisse_oubliee', 'ordonnee_oubliee', 'altitude_oubliee'];

// ═════════ La figure et les points ═════════

/** La figure : les points d'arête sont marqués d'une croix (un sommet n'en a pas). */
const figure = (dims, pts) => {
    const { svg, alt } = svgRepereEspace({ ...dims, points: pts.filter((p) => p.arete).map((p) => ({ nom: p.nom, c: p.c })) });
    return elementFigure(svg, alt);
};

/** La phrase qui situe chaque point d'arête (en projection, une croix seule se lirait à plusieurs endroits). */
const situer = (pts) => {
    const surArete = pts.filter((p) => p.arete);
    if (surArete.length === 0) return '';
    if (surArete.length === 1) return `<p>Le point ${surArete[0].nom} est sur l'arête [${surArete[0].arete}].</p>`;
    return `<p>Les points ${surArete[0].nom} et ${surArete[1].nom} sont sur les arêtes [${surArete[0].arete}] et [${surArete[1].arete}].</p>`;
};

/** Un point d'arête nommé. */
const nommer = (p, nom) => ({ ...p, nom });

/**
 * Un point à trois coordonnées DISTINCTES et NON NULLES : G, ou un point lisible
 * des arêtes [FG], [CG] ou [GH] ; l'ordre de ses trois valeurs est tiré d'abord
 * (tirerPointTroisValeurs), sinon la grandeur seule donnait la place : dans une
 * première version, l'ordonnée était la plus grande valeur dans 78 % des
 * tirages. Renvoie les dimensions du pavé et le point nommé.
 */
const tirerPointTrois = () => {
    const t = tirerPointTroisValeurs();
    const p = t.sommet ? { nom: 'G', c: t.c, arete: null } : nommer(t, nommerPointsRepere(1)[0]);
    return { dims: t.dims, p };
};

// ═════════ Carte 1 : lire l'abscisse, l'ordonnée ou l'altitude (QCM) ═════════

const genComposante = () => {
    const { dims, p } = tirerPointTrois();
    const i = randInt(0, 2);                                   // le mot demandé tourne
    const cle = String(p.c[i]);
    const choix = [
        { v: cle, code: null },
        ...[0, 1, 2].filter((j) => j !== i).map((j) => ({ v: String(p.c[j]), code: 'mauvaise_composante' })),
        { v: '0', code: OUBLI[i] },
    ];
    // Second chemin : la clé est la coordonnée i relue sur les sommets (G) ou sur l'arête nommée ;
    // quatre valeurs distinctes (trois coordonnées distinctes non nulles, et 0).
    const S = sommetsRepere(dims.X, dims.Y, dims.Z);
    if (p.arete ? areteVisible(dims.X, dims.Y, dims.Z, p.c) !== p.arete : S.G[i] !== p.c[i]) throw new Error('gen_183 composante : point');
    if (new Set(choix.map((c) => c.v)).size !== 4) throw new Error('gen_183 composante : choix non distincts');
    const texte = situer([p]) + `<p><b>Quelle est ${LE_MOT[i]} du point ${p.nom} ?</b></p>`;
    const payload = makeQcm(choix.map((c) => c.v), cle, texte,
        { ...opts(L_COMPOSANTE), elements_additionnels: figure(dims, [p]) });
    payload._meta.params = { dims, point: p.nom, coordonnees: p.c, mot: MOTS[i], distracteurs: choix.slice(1).map((c) => c.code) };
    return payload;
};

// ═════════ Carte 2 : que représente ce nombre ? (QCM) ═════════

/** Le mot pris pour un autre : la paire de places échangées. */
const ECHANGE = { '0-1': 'echange_xy', '0-2': 'echange_xz', '1-2': 'echange_yz' };

const genRole = () => {
    const dims = tirerDimsRepere();
    const { X, Y, Z } = dims;
    const S = sommetsRepere(X, Y, Z);
    const i = randInt(0, 2);                                   // la place demandée tourne
    const unique = (p) => p.c.filter((v) => v === p.c[i]).length === 1;
    const sommets = ['C', 'F', 'G', 'H'].map((n) => ({ nom: n, c: S[n], arete: null }));
    const aretes = pointsAretesRepere(dims).filter(unique);
    const p = aretes.length > 0 && randInt(0, 1) === 1
        ? nommer(randomChoice(aretes), nommerPointsRepere(1)[0])
        : randomChoice(sommets);
    if (!unique(p)) throw new Error('gen_183 role : le nombre revient dans le triplet');
    const choix = [0, 1, 2].map((j) => `${LE_MOT[j]} de ${p.nom}`);
    const texte = `<p><b>Dans ${tex(`${p.nom}${tripletLatex(p.c)}`)}, que représente le nombre ${String(p.c[i])} ?</b></p>`;
    const payload = makeQcm(choix, choix[i], texte, { ...opts(L_ROLE), elements_additionnels: figure(dims, [p]) });
    payload._meta.params = {
        dims, point: p.nom, coordonnees: p.c, place: MOTS[i],
        distracteurs: [0, 1, 2].filter((j) => j !== i).map((j) => ECHANGE[[i, j].sort().join('-')]),
    };
    return payload;
};

// ═════════ Carte 3 : quel point ? (QCM de lettres) ═════════

/** Les sommets qui ne diffèrent de c que par UNE coordonnée, avec l'erreur que chacun incarne. */
const voisins = (S, c, exclus = []) => Object.keys(S)
    .filter((n) => !exclus.includes(n))
    .map((n) => ({ nom: n, c: S[n], diff: [0, 1, 2].filter((k) => S[n][k] !== c[k]) }))
    .filter((v) => v.diff.length === 1)
    .map((v) => ({ nom: v.nom, c: v.c, code: OUBLI[v.diff[0]] }));

/** Régime « sommet » : la clé et ses trois voisins (ils partagent deux coordonnées sur trois). */
const regimeSommet = (dims, S) => {
    const cle = randomChoice(['B', 'C', 'D', 'E', 'G', 'H']);
    return { cle: { nom: cle, c: S[cle], arete: null }, faux: voisins(S, S[cle], [cle]), marques: [] };
};

/** Régime « F et son permuté » : K(0 ; X ; Z) sur [EH] (X < Y), deux voisins de F. */
const regimeF = (dims, S) => {
    const { X, Y, Z } = dims;
    const c = [0, X, Z];
    if (!(X < Y) || !pointLisible(X, Y, Z, c)) return null;
    const K = { nom: nommerPointsRepere(1)[0], c, arete: 'EH', code: 'echange_xy' };
    const faux = [K, ...shuffle(voisins(S, S.F, ['F'])).slice(0, 2)];
    return { cle: { nom: 'F', c: S.F, arete: null }, faux, marques: [K] };
};

/** Régime « point d'arête et son permuté » : K sur [EF] (ou [EH]), L son permuté sur l'autre arête. */
const regimeArete = (dims, S) => {
    const { X, Y, Z } = dims;
    const cands = [];
    for (let k = 1; k < X; k++) if (k < Y) cands.push({ c: [k, 0, Z], perm: [0, k, Z], arete: 'EF', aretePerm: 'EH', bouts: ['E', 'F'] });
    for (let k = 1; k < Math.min(X, Y); k++) cands.push({ c: [0, k, Z], perm: [k, 0, Z], arete: 'EH', aretePerm: 'EF', bouts: ['E', 'H'] });
    const ok = cands.filter((q) => pointLisible(X, Y, Z, q.c) && pointLisible(X, Y, Z, q.perm));
    if (ok.length === 0) return null;
    const q = randomChoice(ok);
    const [nK, nL] = nommerPointsRepere(2);
    const K = { nom: nK, c: q.c, arete: q.arete };
    const L = { nom: nL, c: q.perm, arete: q.aretePerm, code: 'echange_xy' };
    const i = q.arete === 'EF' ? 0 : 1;                        // la coordonnée libre de K
    const faux = [L, ...q.bouts.map((n) => ({ nom: n, c: S[n], code: OUBLI[i] }))];
    return { cle: K, faux, marques: [K, L] };
};

const genPoint = () => {
    const dims = tirerDimsRepere();
    const { X, Y, Z } = dims;
    const S = sommetsRepere(X, Y, Z);
    const regime = randomChoice(['sommet', 'F', 'arete']);
    const t = (regime === 'F' && regimeF(dims, S)) || (regime === 'arete' && regimeArete(dims, S)) || regimeSommet(dims, S);
    // Second chemin : une seule des quatre lettres porte le triplet demandé, et les trois autres
    // sont bien des erreurs d'UNE coordonnée ou le permuté.
    const tous = [t.cle, ...t.faux];
    const cleTxt = t.cle.c.join(';');
    if (tous.length !== 4 || new Set(tous.map((q) => q.nom)).size !== 4) throw new Error('gen_183 point : quatre lettres');
    if (tous.filter((q) => q.c.join(';') === cleTxt).length !== 1) throw new Error('gen_183 point : clé non unique');
    t.faux.forEach((q) => {
        const nDiff = [0, 1, 2].filter((k) => q.c[k] !== t.cle.c[k]).length;
        const permute = q.c[0] === t.cle.c[1] && q.c[1] === t.cle.c[0] && q.c[2] === t.cle.c[2];
        if (q.code === 'echange_xy' ? !permute : nDiff !== 1) throw new Error('gen_183 point : distracteur hors famille');
    });
    const marques = t.marques.length ? t.marques : [];
    const texte = situer(marques.map((q) => ({ ...q })))
        + `<p><b>Quel point a pour coordonnées ${tex(tripletLatex(t.cle.c))} ?</b></p>`;
    const payload = makeQcm(tous.map((q) => q.nom), t.cle.nom, texte,
        { ...opts(L_POINT), elements_additionnels: figure(dims, marques) });
    payload._meta.params = { dims, regime: t.marques.length ? (t.cle.arete ? 'arete' : 'F') : 'sommet', cle: t.cle.nom, coordonnees: t.cle.c, distracteurs: t.faux.map((q) => q.code) };
    return payload;
};

// ═════════ Carte 4 : coordonnées communes (V/F), venue de 184 ═════════

const genCommunes = () => {
    const dims = tirerDimsRepere();
    const S = sommetsRepere(dims.X, dims.Y, dims.Z);
    const noms = Object.keys(S);
    const nbVraies = randInt(1, 3);                            // le quota d'abord
    const verites = shuffle([...Array(4)].map((_, j) => j < nbVraies));
    const mots = shuffle([0, 1, 2, randInt(0, 2)]);            // les trois mots présents
    for (let essai = 0; essai < 100; essai++) {
        const vues = new Set();
        const lignes = [];
        verites.forEach((v, j) => {
            const i = mots[j];
            const cands = [];
            noms.forEach((a, ia) => noms.slice(ia + 1).forEach((b) => {
                if ((S[a][i] === S[b][i]) === v && !vues.has(a + b)) cands.push([a, b]);
            }));
            const [a, b] = shuffle(randomChoice(cands));
            vues.add([a, b].sort().join(''));
            lignes.push({ texte: `${a} et ${b} ont la même ${MOTS[i]}.`, v, a, b, i });
        });
        const paires = new Set(lignes.map((l) => [l.a, l.b].sort().join('')));
        if (paires.size !== 4) continue;
        // Second chemin : chaque ligne relue sur les coordonnées des sommets.
        lignes.forEach((l) => { if ((S[l.a][l.i] === S[l.b][l.i]) !== l.v) throw new Error('gen_183 communes : vérité'); });
        const payload = makeVraiFaux(lignes.map((l) => l.texte), lignes.map((l) => (l.v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            { ...opts(L_COMMUNES), elements_additionnels: figure(dims, []) });
        payload._meta.params = { dims, nbVraies, mots: mots.map((i) => MOTS[i]) };
        return payload;
    }
    throw new Error('gen_183 communes : pas de tirage');
};

// ═════════ Carte 5 : abscisse, ordonnée, altitude (drag) ═════════

/** Les pièges : une dimension du pavé à la place d'une coordonnée. */
const PIEGES_MOTS = ['la longueur', 'la hauteur'];

const genMots = () => {
    const { dims, p } = tirerPointTrois();
    if (new Set(p.c).size !== 3) throw new Error('gen_183 mots : triplet');
    const lignes = shuffle([0, 1, 2]).map((i) => `$${p.c[i]}\\ \\text{est}\\ {{${LE_MOT[i]}}}\\ \\text{de ${p.nom}.}$`);
    const phrase = lignes.join('\n');
    const distracteurs = filtrerDistracteurs(phrase, PIEGES_MOTS);
    if (distracteurs.length !== 2) throw new Error('gen_183 mots : pièges');
    const texte = situer([p]) + '<p><b>Complète chaque phrase avec le bon mot.</b> Deux étiquettes sont en trop.</p>';
    const payload = makeTexteTrou(phrase, texte,
        { ...opts(L_MOTS), mode: 'drag', distracteurs, elements_additionnels: figure(dims, [p]) });
    payload._meta.params = { dims, point: p.nom, coordonnees: p.c, distracteurs: ['vocabulaire_des_longueurs', 'vocabulaire_des_longueurs'] };
    return payload;
};

// ═════════ Contrat des distracteurs (données pures, jamais lues à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'figure du cours (svgRepereEspace) : X de 2 à 4 sur la fuyante, Y de 3 à 7, Z de 2 à 5, deux à deux distinctes ; « 0 » et « 1 » seuls écrits sur les axes ; jamais de cote',
        'un point hors des sommets est une croix sur une arête VISIBLE, à 16 unités au moins des croisements de la projection, son arête graduée et nommée dans l\'énoncé',
        'chaque distracteur est une erreur NOMMÉE (codes dans _meta.params.distracteurs), jamais une option inventée ; choix distincts vérifiés (throw sinon)',
    ],
    composante_qcm_unique: {
        support: 'G, ou un point de [FG], [CG] ou [GH] : trois coordonnées distinctes et non nulles ; le mot demandé tourne, un tiers chacun',
        familles: {
            mauvaise_composante: 'une des deux autres coordonnées du point : le mot mal relié à sa place (abscisse, ordonnée, altitude, dans cet ordre)',
            altitude_oubliee: '0 quand on demande l\'altitude : celle du point du dessous, sur la base ABCD',
            abscisse_oubliee: '0 quand on demande l\'abscisse : celle du point de derrière, sur la face ADHE',
            ordonnee_oubliee: '0 quand on demande l\'ordonnée : celle du point de gauche, sur la face ABFE',
        },
        garantie: 'quatre valeurs distinctes par construction (trois coordonnées distinctes non nulles, et 0) ; le rang de la clé parmi les valeurs est mesuré par le test jetable',
    },
    role_qcm_unique: {
        support: 'C, F, G, H ou un point d\'arête dont la coordonnée demandée n\'apparaît qu\'UNE fois dans le triplet ; la place demandée tourne, un tiers chacune',
        familles: {
            echange_xy: 'abscisse et ordonnée échangées (DEPP 4e 2023 q.20 : 35,9 % sur le plan) ; en 3D, l\'habitude du plan, où l\'abscisse est l\'horizontale',
            echange_yz: 'ordonnée et altitude échangées : l\'habitude du plan, où l\'ordonnée est la verticale',
            echange_xz: 'abscisse et altitude échangées (extension de l\'échange aux trois places)',
        },
        supprimees: {
            la_longueur_AD: 'jamais en choix : « 7 est la longueur AD » est vrai aussi, ce serait une seconde bonne réponse',
        },
    },
    point_qcm_unique: {
        support: 'le triplet COMPLET est toujours donné ; trois régimes à parts égales : un sommet (B, C, D, E, G, H) et ses trois voisins ; F et K(0 ; X ; Z) sur [EH] ; un point de [EF] ou [EH] et son permuté sur l\'autre arête',
        familles: {
            abscisse_oubliee: 'le point qui a la même ordonnée et la même altitude : l\'abscisse, la fuyante, mal lue',
            ordonnee_oubliee: 'le point qui a la même abscisse et la même altitude',
            altitude_oubliee: 'le point qui a la même abscisse et la même ordonnée : le point du dessus ou du dessous',
            echange_xy: 'le point marqué dont l\'abscisse et l\'ordonnée sont échangées (DEPP 4e 2023 q.20, 35,9 %), présent dans deux tirages sur trois',
        },
        supprimees: {
            criteres_partiels: '« ordonnée 7 et altitude 0 » avait DEUX réponses parmi les sommets (C et D) : jamais un triplet incomplet',
        },
    },
    mots_texte_trou_drag: {
        pieges: { vocabulaire_des_longueurs: '« la longueur », « la hauteur » : la coordonnée prise pour une dimension du pavé ; passés par filtrerDistracteurs' },
    },
};

// ═════════ Registre et exports ═════════

const GENERATORS = {
    composante_qcm_unique: genComposante,
    role_qcm_unique: genRole,
    point_qcm_unique: genPoint,
    communes_tableau_vf: genCommunes,
    mots_texte_trou_drag: genMots,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux', texte_trou_drag: 'Drag' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'composante', label: L_COMPOSANTE, formatsList: buildFormats('composante') },
    { id: 'role', label: L_ROLE, formatsList: buildFormats('role') },
    { id: 'point', label: L_POINT, formatsList: buildFormats('point') },
    { id: 'communes', label: L_COMMUNES, formatsList: buildFormats('communes') },
    { id: 'mots', label: L_MOTS, formatsList: buildFormats('mots') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { GENERATORS };
